import { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { AnimatePresence } from 'framer-motion'

import Button from '../button/Button'
import TextField from '../text-field/TextField'
import { useImportedListings } from '../../_hooks/use-imported-listings'
import { ImportedListing } from '../../_types/imported-listing'
import {
  buildPreviewMeta,
  resolvePreviewBadgeLabel,
  resolvePreviewBadgeTone,
  validateUrl,
} from './helpers'
import {
  Backdrop,
  Card,
  Header,
  Title,
  Subtitle,
  Form,
  Footer,
  ErrorBanner,
  PreviewCard,
  PreviewImage,
  PreviewBody,
  PreviewTitle,
  PreviewMeta,
  PreviewBadge,
  ScrapeNote,
} from './styled'
import { ImportListingModalProps } from './types'

/**
 * Modal that lets the renter paste a listing URL, server-scrape it, and
 * save the result. Shows a preview card with the scraped fields after
 * the import finishes so the renter can confirm before closing.
 *
 * @param {ImportListingModalProps} props
 * @returns {JSX.Element | null}
 */
const ImportListingModal = ({
  isOpen,
  onClose,
}: ImportListingModalProps): JSX.Element | null => {
  const { importFromUrl } = useImportedListings()
  const [urlValue, setUrlValue] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isImporting, setIsImporting] = useState<boolean>(false)
  const [previewListing, setPreviewListing] = useState<ImportedListing | null>(
    null,
  )

  useEffect(() => {
    if (isOpen === false) {
      setUrlValue('')
      setErrorMessage('')
      setPreviewListing(null)
      setIsImporting(false)
    }
  }, [isOpen])

  const handleUrlChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setUrlValue(event.target.value)
  }

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault()
    setErrorMessage('')

    const validationError = validateUrl(urlValue)
    if (validationError !== null) {
      setErrorMessage(validationError)
      return
    }

    setIsImporting(true)

    try {
      const imported = await importFromUrl(urlValue.trim())
      setPreviewListing(imported)
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Could not import that listing. Try again in a sec.',
      )
    } finally {
      setIsImporting(false)
    }
  }

  const handleBackdropClick = (): void => {
    if (isImporting === true) {
      return
    }

    onClose()
  }

  const stopPropagation = (event: { stopPropagation: () => void }): void => {
    event.stopPropagation()
  }

  if (isOpen === false) {
    return null
  }

  const previewMetaParts =
    previewListing === null ? [] : buildPreviewMeta(previewListing)

  const previewStatus =
    previewListing === null ? null : previewListing.scrapeStatus
  const statusTone = resolvePreviewBadgeTone(previewStatus)
  const statusLabel = resolvePreviewBadgeLabel(previewStatus)

  return (
    <AnimatePresence>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleBackdropClick}
      >
        <Card
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { duration: 0.32 },
          }}
          exit={{ opacity: 0, y: 16, scale: 0.98 }}
          onClick={stopPropagation}
        >
          <Header>
            <Title>Import a place</Title>
            <Subtitle>
              Paste a listing URL from any rental site — we&apos;ll grab the
              details and save it. We&apos;ll re-check daily for price and
              availability changes.
            </Subtitle>
          </Header>

          <Form onSubmit={handleSubmit}>
            <TextField
              label="Listing URL"
              value={urlValue}
              onChange={handleUrlChange}
              placeholder="https://rentals.ca/toronto/..."
              autoFocus
              disabled={isImporting || previewListing !== null}
            />

            {errorMessage !== '' ? (
              <ErrorBanner>{errorMessage}</ErrorBanner>
            ) : null}

            {previewListing !== null ? (
              <div>
                <PreviewCard>
                  <PreviewImage
                    style={{
                      backgroundImage:
                        previewListing.imageUrls.length > 0
                          ? `url(${previewListing.imageUrls[0]})`
                          : undefined,
                    }}
                  />
                  <PreviewBody>
                    <PreviewBadge tone={statusTone}>{statusLabel}</PreviewBadge>
                    <PreviewTitle>{previewListing.title}</PreviewTitle>
                    <PreviewMeta>
                      {previewMetaParts.length === 0 ? (
                        <span>{previewListing.sourceSite}</span>
                      ) : (
                        previewMetaParts.map((part, index) => (
                          <span key={`${part}-${index}`}>{part}</span>
                        ))
                      )}
                    </PreviewMeta>
                  </PreviewBody>
                </PreviewCard>
                {previewListing.scrapeNotes !== '' ? (
                  <ScrapeNote>{previewListing.scrapeNotes}</ScrapeNote>
                ) : null}
              </div>
            ) : null}

            <Footer>
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={isImporting}
              >
                {previewListing === null ? 'Cancel' : 'Done'}
              </Button>
              {previewListing === null ? (
                <Button type="submit" isLoading={isImporting}>
                  Import listing
                </Button>
              ) : null}
            </Footer>
          </Form>
        </Card>
      </Backdrop>
    </AnimatePresence>
  )
}

export default ImportListingModal
